/**
 * Juice engine: cartoon blasts and feedback particles, after Ghost Circuit's CartoonBlasts
 * (src/circuit/rendering/cartoon-blast.js), turned for a side view.
 *
 * A blast is an inked starburst that pops and shrinks, cel puffs blooming outward and cooling
 * through their palette, smoke rolling up behind, a ring running out, sparks and sparkles flung
 * out under gravity, and optional inked chunks that tumble and bounce. Thumps kick dust along a
 * surface. Smaller verbs (puff, sparkles, hearts, ring, glow, chunks, shards, popText, trails)
 * reuse the same pieces.
 *
 * Renderer-agnostic state: `render(drawer, layer)` walks the live particles in paint order and
 * asks the drawer for atlas frames (Pixi sprites in juice-pixi.js, drawImage in
 * juice-canvas.js). Effects use Math.random freely (pure presentation) and never the game RNG;
 * counts are capped, and `lite` halves them.
 *
 * `resolution` (device px per canvas px) picks the large atlas frames for pictures drawn well
 * above their base size; `reduced` (prefers-reduced-motion) drops flashes, glows and rings and
 * halves counts and puff sizes, so nothing flashes at a photosensitive player.
 */
import { resolveBlastPalette, CHUNK_TONES } from "./juice-palettes.js";
import {
  PUFF_PX, PUFF_UNIT, BURST_PX, BURST_R, BURST_VARIANTS, BURST_INK_RATIOS,
  SPARKLE_PX, SPARKLE_R, DOT_PX, DOT_R, RING_PX, RING_RATIOS, ringCenterRadius,
  CHUNK_PX, CHUNK_R, CHUNK_SHAPES, SHARD_PX, SHARD_SHAPES, HEART_PX, STAR_PX, BUBBLE_PX, BUBBLE_R,
  BURST_LG_PX, BURST_LG_R, BURST_LG_VARIANTS, LARGE_PUFF_TONES, LARGE_PUFF_VARIANTS,
  RING_ARC_R, RING_ARCS, RING_ARC_BUCKETS, ringArcGeom,
} from "./juice-atlas.js";

var TAU = Math.PI * 2;
/** Caps: puffs are the costly part (each a stretched picture), so there are fewest of them. */
export var JUICE_CAPS = { puffs: 84, flashes: 16, rings: 24, sparks: 200, chunks: 72, glows: 16, texts: 16 };
/** Trails may fill the puff pool only this far, so a blast always finds room. */
var TRAIL_PUFF_CAP = 48;
/**
 * Most puffs one trail lays along one frame's travel (a fast boulder on a slow frame). The live
 * count stays bounded by maxLength / spacing whatever the frame rate; this only caps a hitch.
 */
var TRAIL_MAX_PER_FRAME = 5;
/** A picture is drawn from the large set once it would be enlarged past this. */
var LARGE_AT = 1.3;

var largePuffTones = Object.create(null);
LARGE_PUFF_TONES.forEach(function (t) { largePuffTones[t] = true; });
/** Draw layers: BACK sits under the world sprites (clipped with them), FRONT over everything. */
export var LAYER_BACK = 0;
export var LAYER_FRONT = 1;

function rand(a, b) {
  return a + Math.random() * (b - a);
}
function ease(k) {
  var u = 1 - k;
  return 1 - u * u * u;
}
function clamp(v, a, b) {
  return v < a ? a : v > b ? b : v;
}
function pick(list) {
  return list[Math.floor(Math.random() * list.length) % list.length];
}
function layerOf(v) {
  return v === "back" || v === LAYER_BACK ? LAYER_BACK : LAYER_FRONT;
}
function range(v, a, b) {
  if (Array.isArray(v)) return rand(v[0], v[1]);
  if (v != null && isFinite(v)) return Number(v);
  return rand(a, b);
}

/**
 * @param {{ enabled?: () => boolean, lite?: boolean }} [opts]
 *   enabled: spawn gate (the game's areRuntimeParticlesEnabled); when false, spawns are no-ops.
 */
export function createJuiceEngine(opts) {
  var o = opts || {};
  var enabled = typeof o.enabled === "function" ? o.enabled : function () { return true; };
  var puffs = [], flashes = [], rings = [], sparks = [], chunks = [], glows = [], texts = [];
  var trails = new Map();
  var pulses = Object.create(null);
  var textBaker = null;

  /** Share of the pieces to spawn: lite (weak device) and reduced motion each halve it. */
  function countCut() {
    return (J.lite ? 0.5 : 1) * (J.reduced ? 0.5 : 1);
  }

  /** One trail piece at (x, y): a puff (kind "puff") or a sparkle / dot, `age` s old already. */
  function emitTrailPiece(x, y, t, life, age) {
    var jit = t.jitter != null ? t.jitter : 3;
    var px = x + rand(-jit, jit), py = y + rand(-jit, jit);
    if (t.kind === "puff") {
      // Trails leave room in the pool for the next blast.
      if (puffs.length >= TRAIL_PUFF_CAP) return;
      J.puff(px, py, {
        tone: t.tone, palette: t.palette, size: (t.size || 8) * rand(0.8, 1.15), life: life || 0.42,
        vx: (t.vx || 0) + rand(-8, 8), vy: t.vy != null ? t.vy : -14, layer: t.layer, age: age,
      });
      return;
    }
    if (sparks.length >= JUICE_CAPS.sparks) return;
    var colors = t.colors || resolveBlastPalette(t.palette || "gold").spark;
    sparks.push({
      x: px, y: py, vx: (t.vx || 0) + rand(-14, 14), vy: (t.vy != null ? t.vy : -10) + rand(-10, 10),
      age: age || 0, life: life || 0.5, size: Math.max(1.4 * J.unit, (t.size || 3.5) * rand(0.75, 1.2)),
      color: pick(colors), shape: t.kind === "dot" ? "dot" : (t.inked ? "sparkle-ink" : "sparkle"),
      rot: rand(0, TAU), spin: rand(-4, 4), g: 0, drag: 1.5, layer: layerOf(t.layer),
    });
  }

  var J = {
    lite: !!o.lite,
    /** prefers-reduced-motion: no flashes, glows or rings; half the pieces, smaller puffs. */
    reduced: !!o.reduced,
    /** Device px per canvas px (renderer resolution): picks the large atlas frames. */
    resolution: 1,
    /** Seconds on the engine clock (advances only through update, so it pauses with the game). */
    time: 0,
    /** The last update's step (s): trails spread a frame's travel over it. */
    lastDt: 0,
    /** Layout unit: ~1 at a 150 px wide flask. Sets ink and minimum spark sizes. */
    unit: 1,

    get busy() {
      return puffs.length + flashes.length + rings.length + sparks.length + chunks.length + glows.length + texts.length > 0;
    },

    get counts() {
      return { puffs: puffs.length, flashes: flashes.length, rings: rings.length, sparks: sparks.length, chunks: chunks.length, glows: glows.length, texts: texts.length };
    },

    clear: function () {
      puffs.length = flashes.length = rings.length = sparks.length = chunks.length = glows.length = texts.length = 0;
      trails.clear();
      pulses = Object.create(null);
    },

    /** Where popText gets its baked lettering: fn(text, color, size) → { canvas, w, h } | null. */
    setTextBaker: function (fn) {
      textBaker = typeof fn === "function" ? fn : null;
    },

    /**
     * A cartoon explosion at (x, y) about `size` px across.
     * @param {number} x
     * @param {number} y
     * @param {{ palette?: string|object, size?: number, reach?: number, fire?: number, smoke?: number,
     *   sparks?: number, chunks?: string|null, chunkCount?: number, flash?: number, glow?: number,
     *   gravity?: number, layer?: string|number, floorY?: number, slow?: number, stars?: number,
     *   reveal?: number }} [opt]
     *   reach: radius of the shock ring (0: none); fire / smoke / sparks scale how much of each;
     *   chunks: a CHUNK_TONES material ("rock", "glass", "gem", "gold") flung out; glow: soft core
     *   light (0–1); stars: share of sparks drawn as inked gold stars; slow: life multiplier;
     *   reveal: radius of something popping in at (x, y) that the blast must not hide (a merge's
     *   new creature): no heart puff, the starburst, glow and smoke go behind it (back layer),
     *   and the fire puffs, sparks, chunks and ring start on its rim and run outward.
     */
    blast: function (x, y, opt) {
      if (!enabled()) return;
      var b = opt || {};
      var P = resolveBlastPalette(b.palette || "fire");
      var size = b.size > 0 ? b.size : 120;
      var S = size / 120;
      var cut = countCut();
      var layer = layerOf(b.layer);
      var slow = b.slow > 0 ? b.slow : 1;
      var flash = b.flash != null ? b.flash : 1;
      // `delay` holds the whole blast back (a second beat after a first blast).
      var wait = b.delay > 0 ? b.delay : 0;
      var R = b.reveal > 0 ? b.reveal : 0;
      var under = R > 0 ? LAYER_BACK : layer;
      // Behind a revealed creature the starburst must reach past it to be seen at all.
      var flashSize = R > 0 ? Math.max(size * 0.44, R * 1.32) * flash : size * 0.44 * flash;
      if (flash > 0) J.flash(x, y - (R > 0 ? 0 : 4 * S), { size: flashSize, palette: P, layer: under, life: 0.2 * slow, delay: wait });
      if (b.glow > 0 && P.glow) J.glow(x, y, { size: Math.max(size * 1.3, R * 3) * b.glow, color: P.glow, life: 0.34 * slow, layer: under, delay: wait });
      if (b.reach > 0) {
        // A revealed creature: the ring is born on its rim instead of crossing its face.
        var start = R > 0 ? clamp((R * 0.95) / b.reach, 0.3, 0.9) : 0.3;
        J.ring(x, y, { radius: b.reach, palette: P, life: 0.36 * slow, layer: layer, width: S, delay: wait, start: start });
      }
      var fire = b.fire != null ? b.fire : 1;
      var puffK = J.reduced ? 0.6 : 1;
      // Fire puffs bloom outward in a ring, one big one at the heart (none when revealing).
      var n = Math.round(5 * fire * cut);
      var ringAt = R > 0 ? R * 1.04 : 6 * S;
      for (var i = 0; i < n && puffs.length < JUICE_CAPS.puffs; i++) {
        var a = (i / n) * TAU + rand(-0.3, 0.3);
        var v = rand(80, 150) * S;
        puffs.push({
          kind: "fire", tones: P.fire, x: x + Math.cos(a) * ringAt, y: y + Math.sin(a) * ringAt * (R > 0 ? 0.92 : 0.84),
          vx: Math.cos(a) * v, vy: Math.sin(a) * v * 0.8 - rand(10, 40) * S,
          age: -rand(0, 0.04) - wait, life: rand(0.36, 0.48) * slow, size: rand(0.8, 1.1) * 24 * S * puffK,
          variant: i % 3, flip: Math.random() < 0.5, layer: layer,
        });
      }
      if (fire > 0 && R <= 0 && puffs.length < JUICE_CAPS.puffs) {
        puffs.push({
          kind: "fire", tones: P.fire, x: x, y: y - 4 * S, vx: 0, vy: -30 * S, age: -wait, life: 0.42 * slow,
          size: 32 * S * Math.min(1, 0.5 + fire) * puffK, variant: 1, flip: false, layer: layer,
        });
      }
      // Smoke rolls up behind as the fire dies (from behind the revealed creature's crown).
      var m = Math.round(4 * (b.smoke != null ? b.smoke : 1) * cut);
      for (var s = 0; s < m && puffs.length < JUICE_CAPS.puffs; s++) {
        var sa = rand(0, TAU), sv = rand(20, 55) * S;
        var sx = R > 0 ? x + rand(-0.55, 0.55) * R : x + Math.cos(sa) * 12 * S;
        var sy = R > 0 ? y - R * rand(0.55, 0.85) : y + Math.sin(sa) * 8 * S;
        puffs.push({
          kind: "smoke", tone: P.smoke, x: sx, y: sy,
          vx: Math.cos(sa) * sv, vy: Math.sin(sa) * sv * 0.5 - rand(30, 55) * S,
          age: -rand(0.1, 0.2) - wait, life: rand(0.55, 0.75) * slow, size: rand(0.8, 1.1) * 19 * S * puffK,
          variant: s % 3, flip: Math.random() < 0.5, layer: under,
        });
      }
      var sparkMul = b.sparks != null ? b.sparks : 1;
      var k = Math.round(14 * sparkMul * cut * clamp(S, 0.5, 1.6));
      var sparkSize = clamp(S, 0.8, 2.2);
      var starShare = b.stars > 0 ? b.stars : 0;
      for (var j = 0; j < k && sparks.length < JUICE_CAPS.sparks; j++) {
        var ka = rand(0, TAU), kv = rand(70, 230) * S;
        var shape = starShare > 0 && Math.random() < starShare ? "star" : (j % 3 === 0 ? "sparkle" : "dot");
        var kr = R > 0 ? R * rand(0.75, 0.95) : 0;
        sparks.push({
          x: x + Math.cos(ka) * kr, y: y + Math.sin(ka) * kr,
          vx: Math.cos(ka) * kv, vy: Math.sin(ka) * kv * 0.85 - rand(30, 90) * S,
          age: -wait, life: rand(0.35, 0.7) * slow, size: rand(2, 4.2) * sparkSize * (shape === "star" ? 1.6 : 1),
          color: P.spark[j % 2], shape: shape, rot: rand(0, TAU), spin: rand(-8, 8),
          g: b.gravity != null ? b.gravity : 260, drag: 2.6, layer: layer,
        });
      }
      if (b.chunks && CHUNK_TONES[b.chunks]) {
        J.chunks(x, y, {
          material: b.chunks, count: Math.round(8 * (b.chunkCount != null ? b.chunkCount : 1)),
          speed: S, size: clamp(S, 0.7, 2), floorY: b.floorY, layer: layer, delay: wait, radius: R * 0.85,
        });
      }
    },

    /**
     * A thump on a surface at y (no fire): dust kicked out sideways along it, two big side puffs,
     * clods flicked up that bounce back on the surface line, and a flat ring lying on it.
     * @param {{ reach?: number, puffs?: number, puffScale?: number, chunks?: string|null, ring?: boolean, palette?: string|object, layer?: string|number }} [opt]
     */
    thump: function (x, y, opt) {
      if (!enabled()) return;
      var t = opt || {};
      var P = resolveBlastPalette(t.palette || "dust");
      var reach = t.reach > 0 ? t.reach : 60;
      var cut = countCut();
      var layer = layerOf(t.layer);
      var n = Math.round((t.puffs != null ? t.puffs : 9) * cut);
      var ps = (t.puffScale > 0 ? t.puffScale : 1) * (J.reduced ? 0.6 : 1);
      if (t.ring !== false) J.ring(x, y, { radius: reach, palette: P, life: 0.34, squash: 0.32, layer: layer, width: reach / 60 });
      for (var i = 0; i < n && puffs.length < JUICE_CAPS.puffs; i++) {
        var a = (i / n) * TAU + rand(-0.2, 0.2);
        var dir = Math.cos(a);
        var d = reach * rand(0.25, 0.35), v = reach * rand(3.2, 4);
        puffs.push({
          kind: "fire", tones: P.fire, x: x + dir * d, y: y - Math.abs(Math.sin(a)) * reach * 0.12,
          vx: dir * v, vy: -Math.abs(Math.sin(a)) * v * 0.3 - rand(10, 30),
          age: -rand(0, 0.03), life: rand(0.34, 0.46), size: reach * rand(0.1, 0.14) * ps,
          variant: i % 3, flip: Math.random() < 0.5, layer: layer,
        });
      }
      for (var side = -1; side <= 1; side += 2) {
        if (puffs.length >= JUICE_CAPS.puffs) break;
        puffs.push({
          kind: "fire", tones: P.fire, x: x + side * reach * 0.3, y: y - reach * 0.04, vx: side * reach * 2.6, vy: -rand(8, 20),
          age: 0, life: 0.5, size: reach * 0.22 * ps, variant: side > 0 ? 0 : 2, flip: side < 0, layer: layer,
        });
      }
      if (t.chunks !== null && CHUNK_TONES[t.chunks || "rock"]) {
        J.chunks(x, y - reach * 0.05, {
          material: t.chunks || "rock", count: 6, speed: reach / 60, size: clamp(reach / 60, 0.7, 1.8),
          floorY: y, up: 1.2, spread: Math.PI * 0.9, layer: layer,
        });
      }
    },

    /** One inked starburst flash (pops, shrinks). Off under reduced motion. */
    flash: function (x, y, opt) {
      if (!enabled() || J.reduced) return;
      var f = opt || {};
      var P = resolveBlastPalette(f.palette || "impact");
      if (flashes.length >= JUICE_CAPS.flashes) flashes.shift();
      flashes.push({
        x: x, y: y, age: f.delay ? -f.delay : 0, life: f.life > 0 ? f.life : 0.2, size: f.size > 0 ? f.size : 40,
        rim: f.rim || P.rim, heart: f.heart || P.flash, variant: Math.floor(Math.random() * BURST_VARIANTS),
        rot: f.rot != null ? f.rot : rand(0, TAU), layer: layerOf(f.layer),
      });
    },

    /**
     * A shock ring running out to `radius` (squash < 1 lays it flat on a surface); `start` is the
     * share of the radius it is born at. Off under reduced motion.
     */
    ring: function (x, y, opt) {
      if (!enabled() || J.reduced) return;
      var r = opt || {};
      var P = resolveBlastPalette(r.palette || "gold");
      if (rings.length >= JUICE_CAPS.rings) rings.shift();
      rings.push({
        x: x, y: y, age: r.delay ? -r.delay : 0, life: r.life > 0 ? r.life : 0.36, r: r.radius > 0 ? r.radius : 40,
        rim: r.rim || P.ringRim || P.rim, ring: r.color || P.ring, squash: r.squash != null ? r.squash : 1,
        width: r.width > 0 ? r.width : 1, start: r.start != null ? r.start : 0.3, layer: layerOf(r.layer),
      });
    },

    /** Soft additive light (emitters only: a blast core, a shield pop). Off under reduced motion. */
    glow: function (x, y, opt) {
      if (!enabled() || J.reduced) return;
      var g = opt || {};
      if (glows.length >= JUICE_CAPS.glows) glows.shift();
      glows.push({
        x: x, y: y, age: g.delay ? -g.delay : 0, life: g.life > 0 ? g.life : 0.3, size: g.size > 0 ? g.size : 60,
        color: g.color || "#fff6c4", alpha: g.alpha != null ? g.alpha : 0.8, layer: layerOf(g.layer),
      });
    },

    /**
     * One cel puff. `tone` is a baked puff tone name; or pass `palette` to cool through its fire ramp.
     * @param {{ tone?: string, palette?: string|object, size?: number, vx?: number, vy?: number,
     *   life?: number, kind?: "fire"|"smoke", variant?: number, layer?: string|number, delay?: number,
     *   age?: number }} [opt] age: a head start (s), for puffs laid along a frame's travel
     */
    puff: function (x, y, opt) {
      if (!enabled() || puffs.length >= JUICE_CAPS.puffs) return;
      var p = opt || {};
      var kind = p.kind === "smoke" ? "smoke" : "fire";
      var entry = {
        kind: kind, x: x, y: y, vx: p.vx || 0, vy: p.vy != null ? p.vy : -20,
        age: p.delay ? -p.delay : (p.age > 0 ? p.age : 0), life: p.life > 0 ? p.life : 0.45,
        size: (p.size > 0 ? p.size : 14) * (J.reduced ? 0.6 : 1),
        variant: p.variant != null ? p.variant % 3 : Math.floor(Math.random() * 3), flip: Math.random() < 0.5,
        layer: layerOf(p.layer), alpha: p.alpha != null ? p.alpha : 1,
      };
      if (p.tone) {
        entry.tone = p.tone;
        entry.tones = null;
      } else {
        var P = resolveBlastPalette(p.palette || "dust");
        if (kind === "smoke") entry.tone = P.smoke;
        else entry.tones = P.fire;
      }
      puffs.push(entry);
    },

    /**
     * Sparkles / dots / hearts / stars flung from (x, y).
     * @param {{ count?: number, colors?: string[], palette?: string|object, speed?: number|number[],
     *   size?: number|number[], life?: number|number[], shape?: "sparkle"|"sparkle-ink"|"dot"|"mix"|"heart"|"star"|"bubble",
     *   gravity?: number, up?: number, angle?: number, spread?: number, drag?: number, radius?: number,
     *   layer?: string|number, delay?: number }} [opt]
     *   angle / spread: direction cone (default all round); radius: start spread round (x, y);
     *   up: extra upward kick (px/s).
     */
    sparkles: function (x, y, opt) {
      if (!enabled()) return;
      var s = opt || {};
      var cut = countCut();
      var count = Math.max(1, Math.round((s.count != null ? s.count : 8) * cut));
      var colors = s.colors || resolveBlastPalette(s.palette || "gold").spark;
      var spread = s.spread != null ? s.spread : TAU;
      var base = s.angle != null ? s.angle : -Math.PI / 2;
      var layer = layerOf(s.layer);
      var minSize = 1.6 * J.unit;
      for (var i = 0; i < count && sparks.length < JUICE_CAPS.sparks; i++) {
        var a = spread >= TAU ? rand(0, TAU) : base + rand(-spread / 2, spread / 2);
        var v = range(s.speed, 60, 180);
        var shape = s.shape || "sparkle";
        if (shape === "mix") shape = i % 3 === 0 ? "sparkle-ink" : (i % 3 === 1 ? "dot" : "sparkle");
        // `onRing` starts every sparkle on the radius (for inward swirls), else anywhere inside.
        var sr = s.radius > 0 ? (s.onRing ? s.radius : rand(0, s.radius)) : 0;
        sparks.push({
          x: x + Math.cos(a) * sr, y: y + Math.sin(a) * sr,
          vx: Math.cos(a) * v, vy: Math.sin(a) * v - (s.up != null ? s.up : 40),
          age: s.delay ? -rand(0, s.delay) : 0, life: range(s.life, 0.35, 0.7),
          size: Math.max(minSize, range(s.size, 2.5, 5)), color: colors[i % colors.length], shape: shape,
          rot: rand(0, TAU), spin: s.spin != null ? s.spin * rand(-1, 1) : rand(-6, 6),
          g: s.gravity != null ? s.gravity : 220, drag: s.drag != null ? s.drag : 2.6, layer: layer,
        });
      }
    },

    /** Inked hearts floating up (heal, revive). */
    hearts: function (x, y, opt) {
      var h = opt || {};
      J.sparkles(x, y, {
        count: h.count != null ? h.count : 6, colors: ["#ffffff"], shape: "heart",
        speed: h.speed || [30, 90], size: h.size || [5, 8], life: h.life || [0.7, 1.1],
        gravity: h.gravity != null ? h.gravity : -60, up: h.up != null ? h.up : 50,
        angle: -Math.PI / 2, spread: h.spread != null ? h.spread : Math.PI * 1.1, drag: 1.4, spin: 1.5,
        radius: h.radius || 0, layer: h.layer, delay: h.delay,
      });
    },

    /**
     * Inked chunks (rock, glass, gem, gold) flung out, tumbling under gravity; with `floorY` they
     * bounce once or twice on that line, then slide to a stop and shrink away.
     * @param {{ material?: string, count?: number, speed?: number, size?: number, floorY?: number,
     *   up?: number, angle?: number, spread?: number, gravity?: number, life?: number|number[],
     *   layer?: string|number, radius?: number }} [opt] radius: start this far out (a revealed creature's rim)
     */
    chunks: function (x, y, opt) {
      if (!enabled()) return;
      var c = opt || {};
      var mat = CHUNK_TONES[c.material] ? c.material : "rock";
      var cut = countCut();
      var q = Math.max(1, Math.round((c.count != null ? c.count : 8) * cut));
      var speed = c.speed > 0 ? c.speed : 1;
      var sz = c.size > 0 ? c.size : 1;
      var spread = c.spread != null ? c.spread : TAU;
      var base = c.angle != null ? c.angle : -Math.PI / 2;
      var out = 4 + (c.radius > 0 ? c.radius : 0);
      for (var i = 0; i < q && chunks.length < JUICE_CAPS.chunks; i++) {
        var a = spread >= TAU ? (i / q) * TAU + rand(-0.4, 0.4) : base + rand(-spread / 2, spread / 2);
        var v = rand(90, 210) * speed;
        chunks.push({
          frame: "chunk:" + mat + ":" + (i % 2) + ":" + Math.floor(Math.random() * CHUNK_SHAPES), fpx: CHUNK_PX, fr: CHUNK_R,
          x: x + Math.cos(a) * out, y: y + Math.sin(a) * out,
          vx: Math.cos(a) * v, vy: Math.sin(a) * v - rand(90, 170) * speed * (c.up != null ? c.up : 1),
          rot: rand(0, TAU), spin: rand(-12, 12), age: c.delay ? -c.delay : 0, life: range(c.life, 0.9, 1.3),
          size: rand(4.5, 7.5) * sz, g: c.gravity != null ? c.gravity : 900, floorY: c.floorY,
          layer: layerOf(c.layer),
        });
      }
    },

    /** Glass slivers: the flask's breaking bits, spinning fast (confetti of glass). */
    shards: function (x, y, opt) {
      if (!enabled()) return;
      var c = opt || {};
      var cut = countCut();
      var q = Math.max(1, Math.round((c.count != null ? c.count : 10) * cut));
      var speed = c.speed > 0 ? c.speed : 1;
      var sz = c.size > 0 ? c.size : 1;
      var spread = c.spread != null ? c.spread : TAU;
      var base = c.angle != null ? c.angle : -Math.PI / 2;
      var w = c.width > 0 ? c.width : 0;
      for (var i = 0; i < q && chunks.length < JUICE_CAPS.chunks; i++) {
        var a = spread >= TAU ? rand(0, TAU) : base + rand(-spread / 2, spread / 2);
        var v = rand(120, 300) * speed;
        chunks.push({
          frame: "shard:" + Math.floor(Math.random() * SHARD_SHAPES), fpx: SHARD_PX, fr: 21,
          x: x + (w ? rand(-w / 2, w / 2) : 0), y: y + rand(-4, 4),
          vx: Math.cos(a) * v, vy: Math.sin(a) * v - rand(80, 200) * speed,
          rot: rand(0, TAU), spin: rand(-18, 18), age: 0, life: range(c.life, 0.8, 1.25),
          size: rand(5, 9.5) * sz, g: c.gravity != null ? c.gravity : 1100, floorY: c.floorY,
          layer: layerOf(c.layer),
        });
      }
    },

    /**
     * Inked lettering that pops in with overshoot, rises and fades (needs a text baker).
     * @param {{ color?: string, size?: number, life?: number, rise?: number, layer?: string|number, delay?: number }} [opt]
     */
    popText: function (x, y, text, opt) {
      if (!textBaker) return;
      var t = opt || {};
      var size = t.size > 0 ? t.size : 26;
      var baked = textBaker(String(text), t.color || "#ffd45c", size);
      if (!baked) return;
      if (texts.length >= JUICE_CAPS.texts) texts.shift();
      texts.push({
        x: x, y: y, canvas: baked.canvas, w: baked.w, h: baked.h, age: t.delay ? -t.delay : 0,
        life: t.life > 0 ? t.life : 0.9, rise: t.rise != null ? t.rise : size * 1.2, layer: layerOf(t.layer),
      });
    },

    /**
     * Trail emitter for a moving thing (falling objects): call every frame with its position.
     * By default it emits a particle every `every` seconds of engine time. With `spacing` (px) it
     * emits by distance instead: puffs are laid along the segment travelled since the last one,
     * `spacing` apart (at most TRAIL_MAX_PER_FRAME per frame, spread evenly when capped), so a
     * fast mover leaves one continuous plume rather than a dotted line; `maxLength` (px) shortens
     * the puffs' life at speed so the plume keeps about that length. Stale trails are pruned in
     * update.
     * @param {string|number} id stable id of the thing
     * @param {{ every?: number, spacing?: number, maxLength?: number, kind?: "puff"|"sparkle"|"dot",
     *   tone?: string, palette?: string|object, colors?: string[], size?: number, life?: number,
     *   jitter?: number, vx?: number, vy?: number, layer?: string|number, inked?: boolean }} [opt]
     */
    trail: function (id, x, y, opt) {
      var t = opt || {};
      var tr = trails.get(id);
      if (!tr) {
        tr = { last: J.time - Math.random() * (t.every || 0.08), seen: J.time, x: x, y: y, px: x, py: y };
        trails.set(id, tr);
      }
      tr.seen = J.time;
      // Speed from this frame's step (the emit anchor may lag several frames behind on a slow mover).
      var fdx = x - tr.px, fdy = y - tr.py;
      var speed = J.lastDt > 0 ? Math.sqrt(fdx * fdx + fdy * fdy) / J.lastDt : 0;
      tr.px = x;
      tr.py = y;
      var every = (t.every > 0 ? t.every : 0.08) * (J.lite ? 2 : 1);
      // Lite / reduced: a shorter plume rather than wider gaps (it must still read as one trail).
      var thin = J.lite || J.reduced;
      var spacing = t.spacing > 0 ? t.spacing * (thin ? 1.25 : 1) : 0;
      if (spacing > 0) {
        var dx = x - tr.x, dy = y - tr.y;
        var d = Math.sqrt(dx * dx + dy * dy);
        if (d > spacing * 30) {
          // A jump, not travel (a trail picked up again after a gap): start over from here.
          tr.x = x;
          tr.y = y;
          return;
        }
        if (d >= spacing) {
          var n = Math.floor(d / spacing);
          var step = spacing;
          if (n > TRAIL_MAX_PER_FRAME) {
            n = TRAIL_MAX_PER_FRAME;
            step = d / n;
          }
          var ux = dx / d, uy = dy / d;
          var life = t.life || 0.42;
          var maxLen = t.maxLength > 0 ? t.maxLength * (thin ? 0.6 : 1) : 0;
          if (maxLen > 0 && speed > 0) life = clamp(maxLen / speed, life * 0.45, life);
          var x0 = tr.x, y0 = tr.y;
          tr.x = x0 + ux * step * n;
          tr.y = y0 + uy * step * n;
          tr.last = J.time;
          if (!enabled()) return;
          var dt = J.lastDt > 0 ? J.lastDt : 0;
          for (var k = 1; k <= n; k++) {
            // Points further back were passed earlier (within this frame at most): older.
            var along = step * k;
            var back = d - along;
            emitTrailPiece(x0 + ux * along, y0 + uy * along, t, life, speed > 0 ? Math.min(dt, back / speed) : 0);
          }
          return;
        }
        // Slow mover: a wisp now and then, from where it is.
        if (J.time - tr.last < every) return;
        tr.last = J.time;
        tr.x = x;
        tr.y = y;
        if (enabled()) emitTrailPiece(x, y, t, t.life || 0.42, 0);
        return;
      }
      if (J.time - tr.last < every) return;
      tr.last = J.time;
      if (!enabled()) return;
      emitTrailPiece(x, y, t, t.life, 0);
    },

    /**
     * A named damped spring other drawers can read for squash (e.g. "flask" kicked on a hit).
     * @param {string} name
     * @param {number} amount starting displacement (signed)
     * @param {{ freq?: number, damp?: number }} [opt] rad/s and 1/s
     */
    pulse: function (name, amount, opt) {
      var p = opt || {};
      var cur = J.pulseValue(name);
      pulses[name] = {
        amp: clamp(cur + amount, -2, 2), t0: J.time,
        freq: p.freq > 0 ? p.freq : 30, damp: p.damp > 0 ? p.damp : 9,
      };
    },

    /** Current value of a named spring (0 at rest). */
    pulseValue: function (name) {
      var p = pulses[name];
      if (!p) return 0;
      var dt = J.time - p.t0;
      var e = Math.exp(-p.damp * dt);
      if (e < 0.01) {
        delete pulses[name];
        return 0;
      }
      return p.amp * e * Math.cos(p.freq * dt);
    },

    /** Advance everything by `dt` seconds (0 while paused). */
    update: function (dt) {
      if (!(dt > 0)) {
        J.lastDt = 0;
        return;
      }
      J.time += dt;
      J.lastDt = dt;
      var n = 0, i, p;
      for (i = 0; i < puffs.length; i++) {
        p = puffs[i];
        p.age += dt;
        if (p.age > p.life) continue;
        if (p.age > 0) {
          var d = Math.exp(-(p.kind === "fire" ? 5 : 2) * dt);
          p.vx *= d;
          p.vy = p.vy * d - (p.kind === "smoke" ? 16 : 8) * dt;
          p.x += p.vx * dt;
          p.y += p.vy * dt;
        }
        puffs[n++] = p;
      }
      puffs.length = n;
      n = 0;
      for (i = 0; i < flashes.length; i++) { p = flashes[i]; p.age += dt; if (p.age < p.life) flashes[n++] = p; }
      flashes.length = n;
      n = 0;
      for (i = 0; i < rings.length; i++) { p = rings[i]; p.age += dt; if (p.age < p.life) rings[n++] = p; }
      rings.length = n;
      n = 0;
      for (i = 0; i < glows.length; i++) { p = glows[i]; p.age += dt; if (p.age < p.life) glows[n++] = p; }
      glows.length = n;
      n = 0;
      for (i = 0; i < texts.length; i++) { p = texts[i]; p.age += dt; if (p.age < p.life) texts[n++] = p; }
      texts.length = n;
      n = 0;
      for (i = 0; i < sparks.length; i++) {
        p = sparks[i];
        p.age += dt;
        if (p.age >= p.life) continue;
        if (p.age > 0) {
          var sd = Math.exp(-p.drag * dt);
          p.vx *= sd;
          p.vy = p.vy * sd + p.g * dt;
          p.x += p.vx * dt;
          p.y += p.vy * dt;
          p.rot += p.spin * dt;
        }
        sparks[n++] = p;
      }
      sparks.length = n;
      n = 0;
      for (i = 0; i < chunks.length; i++) {
        p = chunks[i];
        p.age += dt;
        if (p.age >= p.life) continue;
        if (p.age < 0) {
          chunks[n++] = p;
          continue;
        }
        // Fall, bounce once or twice on the floor line losing most of the height, then slide.
        p.vy += p.g * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.rot += p.spin * dt;
        if (p.floorY != null && p.y >= p.floorY && p.vy > 0) {
          p.y = p.floorY;
          if (p.vy > 140) {
            p.vy *= -0.35;
            p.vx *= 0.6;
            p.spin *= 0.5;
          } else {
            p.vy = 0;
            var fd = Math.exp(-8 * dt);
            p.vx *= fd;
            p.spin *= fd;
          }
        }
        chunks[n++] = p;
      }
      chunks.length = n;
      if (trails.size) {
        trails.forEach(function (tr, key) {
          if (J.time - tr.seen > 1) trails.delete(key);
        });
      }
    },

    /**
     * Paint one layer through `drawer`:
     *   drawer.frame(key, x, y, w, h, rotation, alpha, tint|null, blend) — atlas frame centred on
     *     (x, y), w < 0 mirrors; tint recolours a white mask; blend "add" for soft light; returns
     *     false when the atlas has no such frame (the large set before it is baked), and the
     *     engine then draws the base frame instead;
     *   drawer.canvas(canvas, x, y, w, h, rotation, alpha) — a baked text picture.
     * Order (back to front): rings, smoke, glows, starbursts, fire puffs, chunks, sparks, text.
     */
    render: function (drawer, layer) {
      var L = layerOf(layer);
      var i, p, k, s;
      var unit = J.unit;
      var res = J.resolution > 0 ? J.resolution : 1;
      for (i = 0; i < rings.length; i++) {
        p = rings[i];
        if (p.layer !== L || p.age < 0) continue;
        k = p.age / p.life;
        var e = 1 - k;
        var rr = p.r * (p.start + (1 - p.start) * ease(k));
        drawRing(drawer, p.x, p.y, rr, p.squash, (7 * e + 1) * p.width * unit, p.rim, 0.75 * e, res);
        drawRing(drawer, p.x, p.y, rr, p.squash, (2.4 * e + 0.6) * p.width * unit, p.ring, e, res);
      }
      for (i = 0; i < puffs.length; i++) {
        p = puffs[i];
        if (p.layer !== L || p.kind !== "smoke" || p.age <= 0) continue;
        k = p.age / p.life;
        s = p.size * (k < 0.3 ? 0.45 + 0.55 * ease(k / 0.3) : 1 - 0.85 * Math.pow((k - 0.3) / 0.7, 1.6));
        var sa = 0.85 * (k > 0.6 ? 1 - ((k - 0.6) / 0.4) * 0.7 : 1) * (p.alpha != null ? p.alpha : 1);
        drawPuff(drawer, p, p.tone, s, sa, res);
      }
      for (i = 0; i < glows.length; i++) {
        p = glows[i];
        if (p.layer !== L || p.age < 0) continue;
        k = p.age / p.life;
        var gs = p.size * (0.7 + 0.3 * ease(Math.min(1, k * 3)));
        drawer.frame("glow", p.x, p.y, gs, gs, 0, p.alpha * (1 - k) * (1 - k), p.color, "add");
      }
      for (i = 0; i < flashes.length; i++) {
        p = flashes[i];
        if (p.layer !== L || p.age < 0) continue;
        k = p.age / p.life;
        s = p.size * (k < 0.3 ? 0.5 + 0.5 * ease(k / 0.3) : 1 - (0.55 * (k - 0.3)) / 0.7);
        var fa = k < 0.6 ? 1 : 1 - (k - 0.6) / 0.4;
        var bucket = burstInkBucket(s, unit);
        // A big burst (thinnest ink) is drawn from the large set when the base would be enlarged.
        if (bucket === 0 && s * res > BURST_R * LARGE_AT) {
          var lv = p.variant % BURST_LG_VARIANTS;
          var ld = (BURST_LG_PX * s) / BURST_LG_R;
          if (drawer.frame(BURST_LG_KEYS.ink[lv], p.x, p.y, ld, ld, p.rot, fa, null, null) !== false) {
            drawer.frame(BURST_LG_KEYS.fill[lv], p.x, p.y, ld, ld, p.rot, fa, p.rim, null);
            drawer.frame(BURST_LG_KEYS.heart[lv], p.x, p.y, ld, ld, p.rot, fa, p.heart, null);
            continue;
          }
        }
        var dim = (BURST_PX * s) / BURST_R;
        drawer.frame(BURST_KEYS.ink[p.variant][bucket], p.x, p.y, dim, dim, p.rot, fa, null, null);
        drawer.frame(BURST_KEYS.fill[p.variant], p.x, p.y, dim, dim, p.rot, fa, p.rim, null);
        drawer.frame(BURST_KEYS.heart[p.variant], p.x, p.y, dim, dim, p.rot, fa, p.heart, null);
      }
      for (i = 0; i < puffs.length; i++) {
        p = puffs[i];
        if (p.layer !== L || p.kind !== "fire" || p.age <= 0) continue;
        k = p.age / p.life;
        s = p.size * (k < 0.22 ? 0.35 + 0.65 * ease(k / 0.22) : 1 - Math.pow((k - 0.22) / 0.78, 1.4));
        var tone = p.tones ? p.tones[k < 0.3 ? 0 : k < 0.62 ? 1 : 2] : p.tone;
        drawPuff(drawer, p, tone, s, p.alpha != null ? p.alpha : 1, res);
      }
      for (i = 0; i < chunks.length; i++) {
        p = chunks[i];
        if (p.layer !== L || p.age < 0) continue;
        k = p.age / p.life;
        s = p.size * (k > 0.75 ? 1 - (k - 0.75) / 0.25 : 1);
        if (s <= 0.2) continue;
        var cd = (p.fpx * s) / p.fr;
        drawer.frame(p.frame, p.x, p.y, cd, cd, p.rot, 1, null, null);
      }
      for (i = 0; i < sparks.length; i++) {
        p = sparks[i];
        if (p.layer !== L || p.age < 0) continue;
        k = p.age / p.life;
        var r = p.size * (1 - k);
        if (r <= 0.15) continue;
        if (p.shape === "dot") {
          var dd = (r * 0.6 * DOT_PX) / DOT_R;
          drawer.frame("dot", p.x, p.y, dd, dd, 0, 1, p.color, null);
        } else if (p.shape === "heart") {
          // Hearts hold their size and fade instead of shrinking to a speck.
          var hk = p.size * (k < 0.15 ? 0.4 + 0.6 * ease(k / 0.15) : 1);
          var hd = (hk * 2 * HEART_PX) / 48;
          drawer.frame("heart", p.x, p.y, hd, hd, Math.sin(p.rot) * 0.25, k > 0.6 ? 1 - (k - 0.6) / 0.4 : 1, null, null);
        } else if (p.shape === "bubble") {
          // Bubbles hold their size (a slight wobble) and pop out at the end.
          var bk = p.size * (k < 0.12 ? 0.5 + 0.5 * ease(k / 0.12) : 1) * (1 + 0.08 * Math.sin(p.rot * 3));
          var bd = (bk * BUBBLE_PX) / BUBBLE_R;
          drawer.frame("bubble", p.x, p.y, bd * (1 + 0.06 * Math.sin(p.age * 19)), bd * (1 - 0.06 * Math.sin(p.age * 19)), 0, k > 0.8 ? 1 - (k - 0.8) / 0.2 : 1, null, null);
        } else if (p.shape === "star") {
          var sd2 = (r * 2.2 * STAR_PX) / 38;
          drawer.frame("star", p.x, p.y, sd2, sd2, p.rot, 1, null, null);
        } else {
          var sz = (r * 1.8 * SPARKLE_PX) / SPARKLE_R;
          if (p.shape === "sparkle-ink") drawer.frame("sparkle-ink", p.x, p.y, sz, sz, p.rot, 1, null, null);
          drawer.frame("sparkle", p.x, p.y, sz, sz, p.rot, 1, p.color, null);
        }
      }
      for (i = 0; i < texts.length; i++) {
        p = texts[i];
        if (p.layer !== L || p.age < 0) continue;
        k = p.age / p.life;
        // Pop in with overshoot (1.18 → 1), hold, then rise and fade.
        var pop = k < 0.16 ? 0.55 + 0.63 * ease(k / 0.16) : (k < 0.3 ? 1.18 - 0.18 * ease((k - 0.16) / 0.14) : 1);
        var ta = k < 0.7 ? 1 : 1 - (k - 0.7) / 0.3;
        var ty = p.y - p.rise * ease(Math.min(1, k * 1.1));
        drawer.canvas(p.canvas, p.x, ty, p.w * pop, p.h * pop, 0, ta);
      }
    },
  };
  return J;
}

function burstInkBucket(radius, unit) {
  // Pick the baked outline weight closest to ~2.2 px on screen (scaled with the layout).
  var want = Math.max(1.4, 2.2 * unit);
  var best = 0, bestErr = Infinity;
  for (var i = 0; i < BURST_INK_RATIOS.length; i++) {
    var err = Math.abs(BURST_INK_RATIOS[i] * radius - want);
    if (err < bestErr) { bestErr = err; best = i; }
  }
  return best;
}

// Atlas keys built once (render runs per particle per frame: no string building there).
var BURST_KEYS = { ink: [], fill: [], heart: [] };
for (var bv = 0; bv < BURST_VARIANTS; bv++) {
  BURST_KEYS.fill.push("burst-fill:" + bv);
  BURST_KEYS.heart.push("burst-heart:" + bv);
  BURST_KEYS.ink.push(BURST_INK_RATIOS.map(function (_r, bi) { return "burst-ink:" + bv + ":" + bi; }));
}
var BURST_LG_KEYS = { ink: [], fill: [], heart: [] };
for (var lb = 0; lb < BURST_LG_VARIANTS; lb++) {
  BURST_LG_KEYS.fill.push("burstL-fill:" + lb);
  BURST_LG_KEYS.heart.push("burstL-heart:" + lb);
  BURST_LG_KEYS.ink.push("burstL-ink:" + lb);
}
var RING_KEYS = RING_RATIOS.map(function (_k, i) { return "ring:" + i; });
var ARC_KEYS = [];
var ARC_DIRS = [];
for (var ai = 0; ai < RING_ARC_BUCKETS; ai++) ARC_KEYS.push("arc:" + ai);
for (var ad = 0; ad < RING_ARCS; ad++) {
  var th = (ad / RING_ARCS) * TAU;
  ARC_DIRS.push({ c: Math.cos(th), s: Math.sin(th), rot: th - Math.PI / 2 });
}
var puffKeyMemo = Object.create(null);
function puffKey(tone, variant, large) {
  var m = puffKeyMemo[tone];
  if (!m) {
    m = puffKeyMemo[tone] = [[], []];
    for (var v = 0; v < 3; v++) {
      m[0].push("puff:" + tone + ":" + v);
      m[1].push("puffL:" + tone + ":" + (v % LARGE_PUFF_VARIANTS));
    }
  }
  return m[large ? 1 : 0][variant];
}

function drawRing(drawer, x, y, r, squash, width, color, alpha, res) {
  if (!(r > 0.5) || !(alpha > 0.01)) return;
  var want = width / r;
  var best = 0, bestErr = Infinity;
  for (var i = 0; i < RING_RATIOS.length; i++) {
    var err = Math.abs(RING_RATIOS[i] - want);
    if (err < bestErr) { bestErr = err; best = i; }
  }
  // A big round ring is eight arcs of a 480 px ring (a thin stroke enlarged 3–8× from the
  // 128 px frame turns into a soft smear on DPR 2); flat and thick rings stay one frame.
  if (squash === 1 && best < RING_ARC_BUCKETS && r * (res || 1) > ringCenterRadius(RING_RATIOS[best]) * 1.25) {
    var g = ringArcGeom(best);
    var sc = r / RING_ARC_R;
    var aw = g.w * sc, ah = g.h * sc, off = g.offset * sc;
    var d0 = ARC_DIRS[0];
    if (drawer.frame(ARC_KEYS[best], x + d0.c * off, y + d0.s * off, aw, ah, d0.rot, alpha, color, null) !== false) {
      for (var a = 1; a < RING_ARCS; a++) {
        var d = ARC_DIRS[a];
        drawer.frame(ARC_KEYS[best], x + d.c * off, y + d.s * off, aw, ah, d.rot, alpha, color, null);
      }
      return;
    }
  }
  var dim = (RING_PX * r) / ringCenterRadius(RING_RATIOS[best]);
  drawer.frame(RING_KEYS[best], x, y, dim, dim * squash, 0, alpha, color, null);
}

function drawPuff(drawer, p, tone, s, alpha, res) {
  if (!(s > 0.3) || !(alpha > 0.01) || !tone) return;
  var d = 2 * s * PUFF_UNIT;
  var w = p.flip ? -d : d;
  if (largePuffTones[tone] && d * (res || 1) > PUFF_PX * LARGE_AT) {
    if (drawer.frame(puffKey(tone, p.variant, true), p.x, p.y, w, d, 0, alpha, null, null) !== false) return;
  }
  drawer.frame(puffKey(tone, p.variant, false), p.x, p.y, w, d, 0, alpha, null, null);
}
